"use client";

import {JSX} from "react";

import {useTranslation} from "@/shared/i18n";
import {Typography} from "@/shared/ui/data-display/typography";

import "@/features/rental/check-availability/styles/price-header.scss";

interface Props {
    /** Initial price per night (from unit data, before quote) */
    initialPrice?: number;
    /** Total price from quote (when dates selected) */
    totalPrice?: string | null;
    /** Currency symbol */
    currencySymbol?: string;
    /** Loading state */
    loading?: boolean;
    /** Whether dates are selected */
    hasDates?: boolean;
}

export function PriceHeader({initialPrice, totalPrice, currencySymbol = "$", loading = false, hasDates = false}: Props): JSX.Element | null {
    const {t} = useTranslation("booking");

    // Show skeleton when loading and dates are selected
    if (loading && hasDates) {
        return (
            <div className="price-header price-header--loading">
                <span className="price-header__skeleton" />
                <span className="price-header__skeleton price-header__skeleton--small" />
            </div>
        );
    }

    // Show total price with "Total, fees incl." label (dates selected)
    if (totalPrice) {
        return (
            <div className="price-header">
                <Typography as="div" size={{desktop: 24, mobile: 20}} weight={700} className="price-header__value">
                    {totalPrice}
                </Typography>
                <Typography as="div" size={14} weight={400} className="price-header__note">
                    {t("totalExclTaxes")}
                </Typography>
            </div>
        );
    }

    if (!initialPrice) {
        return null;
    }

    return (
        <div className="price-header">
            <Typography as="div" size={{desktop: 24, mobile: 20}} weight={700} className="price-header__value">
                {currencySymbol}
                {initialPrice}
            </Typography>
            <Typography as="div" size={14} weight={400} className="price-header__note">
                {t("avgPerNight")}
            </Typography>
        </div>
    );
}
